
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const ProcessPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-blue-600 to-purple-700">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Investment Process</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Learn about our comprehensive investment process from initial consultation to project completion and returns
          </p>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our 6-Step Investment Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures successful project delivery and optimal returns for our investors
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white rounded-lg p-8 shadow-lg border-t-4 border-blue-600">
                <div className="absolute -top-4 left-8 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  1
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Initial Consultation</h3>
                  <p className="text-gray-700 mb-4">
                    Meet with our investment team to discuss your goals, risk tolerance, and investment preferences.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Portfolio review and assessment</li>
                    <li>• Investment goal alignment</li>
                    <li>• Risk tolerance evaluation</li>
                    <li>• Timeline discussion</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white rounded-lg p-8 shadow-lg border-t-4 border-blue-600">
                <div className="absolute -top-4 left-8 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  2
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Due Diligence</h3>
                  <p className="text-gray-700 mb-4">
                    Comprehensive review of available investment opportunities with detailed financial analysis.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Market analysis and projections</li>
                    <li>• Financial modeling and ROI calculations</li>
                    <li>• Site evaluation and feasibility studies</li>
                    <li>• Legal and regulatory compliance</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white rounded-lg p-8 shadow-lg border-t-4 border-blue-600">
                <div className="absolute -top-4 left-8 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  3
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Investment Agreement</h3>
                  <p className="text-gray-700 mb-4">
                    Finalize investment terms and execute legal agreements with full transparency and protection.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Contract negotiation and review</li>
                    <li>• Investment structure finalization</li>
                    <li>• Legal documentation completion</li>
                    <li>• Fund transfer and escrow setup</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative">
              <div className="bg-white rounded-lg p-8 shadow-lg border-t-4 border-green-600">
                <div className="absolute -top-4 left-8 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  4
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Project Development</h3>
                  <p className="text-gray-700 mb-4">
                    Professional project management and construction with regular updates and milestone reporting.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Site preparation and permits</li>
                    <li>• Construction management</li>
                    <li>• Quality control and inspections</li>
                    <li>• Regular progress reporting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative">
              <div className="bg-white rounded-lg p-8 shadow-lg border-t-4 border-green-600">
                <div className="absolute -top-4 left-8 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  5
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Launch & Operations</h3>
                  <p className="text-gray-700 mb-4">
                    Grand opening and operational launch with professional marketing and management services.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Marketing campaign launch</li>
                    <li>• Booking platform integration</li>
                    <li>• Staff training and onboarding</li>
                    <li>• Guest experience optimization</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 6 */}
            <div className="relative">
              <div className="bg-white rounded-lg p-8 shadow-lg border-t-4 border-green-600">
                <div className="absolute -top-4 left-8 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  6
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Returns & Reporting</h3>
                  <p className="text-gray-700 mb-4">
                    Ongoing performance monitoring with regular financial reporting and return distributions.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Monthly financial reporting</li>
                    <li>• Quarterly return distributions</li>
                    <li>• Performance analytics and insights</li>
                    <li>• Ongoing optimization strategies</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Typical Project Timeline</h2>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
            
            <div className="space-y-12">
              {/* Month 1-2 */}
              <div className="relative flex items-center">
                <div className="flex-1 pr-8 text-right">
                  <h3 className="text-xl font-bold text-gray-900">Months 1-2</h3>
                  <p className="text-gray-700">Initial consultation, due diligence, and investment agreement finalization</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full"></div>
                <div className="flex-1 pl-8"></div>
              </div>
              
              {/* Month 3-8 */}
              <div className="relative flex items-center">
                <div className="flex-1 pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full"></div>
                <div className="flex-1 pl-8">
                  <h3 className="text-xl font-bold text-gray-900">Months 3-8</h3>
                  <p className="text-gray-700">Site preparation, permits, construction, and development completion</p>
                </div>
              </div>
              
              {/* Month 9-10 */}
              <div className="relative flex items-center">
                <div className="flex-1 pr-8 text-right">
                  <h3 className="text-xl font-bold text-gray-900">Months 9-10</h3>
                  <p className="text-gray-700">Marketing launch, staff training, and operational setup</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full"></div>
                <div className="flex-1 pl-8"></div>
              </div>
              
              {/* Month 11+ */}
              <div className="relative flex items-center">
                <div className="flex-1 pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full"></div>
                <div className="flex-1 pl-8">
                  <h3 className="text-xl font-bold text-gray-900">Month 11+</h3>
                  <p className="text-gray-700">Full operations, revenue generation, and regular return distributions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Requirements */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Investment Requirements</h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Minimum Investment Levels</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700">Individual Projects</span>
                  <span className="font-bold text-blue-600">$50,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700">Portfolio Investment</span>
                  <span className="font-bold text-blue-600">$100,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-700">Premium Partnership</span>
                  <span className="font-bold text-blue-600">$250,000</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-700">Institutional Investment</span>
                  <span className="font-bold text-blue-600">$500,000+</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Investor Qualifications</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Accredited Investor Status</div>
                    <div className="text-gray-700 text-sm">Meet SEC accredited investor requirements</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Financial Verification</div>
                    <div className="text-gray-700 text-sm">Provide proof of funds and financial statements</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Investment Experience</div>
                    <div className="text-gray-700 text-sm">Demonstrated experience in real estate or alternative investments</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Risk Understanding</div>
                    <div className="text-gray-700 text-sm">Acknowledge and understand investment risks</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support & Communication */}
      <section className="py-20 bg-blue-50 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Ongoing Support & Communication</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-700">
                Personal investment advisor available for questions and guidance throughout the investment lifecycle
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-chart-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Regular Reporting</h3>
              <p className="text-gray-700">
                Monthly financial reports, quarterly performance updates, and annual comprehensive reviews
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Investor Events</h3>
              <p className="text-gray-700">
                Exclusive investor meetings, property tours, and networking events with fellow investors
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-blue-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Investment Journey?</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Schedule your initial consultation today and discover how our proven process can help you achieve your investment goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation-scheduling"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/catalog"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Download Process Guide
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProcessPage;
